/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { MetricDataPoint, REVERSE_PERIOD_KEYS, Periods, Measure } from 'cio/utils/metrics/common';
import { formatNumber } from 'cio/helpers/format-number';
import { getMinOfArray, getMaxOfArray } from 'cio/utils/array';
import moment from 'moment-timezone';
import { addDashedZonesToIncompleteDataPoints } from 'cio/utils/graphs';
import merge from 'lodash.merge';
import { Grouping } from 'cio/utils/metrics/resolution';
import type { Options, SVGElement, Chart } from 'highcharts';
import { TimeRange } from 'cio/utils/time';
import { MeasureType } from 'cio/utils/metrics/measure';
import { JourneyMetricsMeasure } from 'cio/utils/journey-metrics';

interface HcSmoothGradientLineChartArgs {
  data: MetricDataPoint[];
  title: string;
  timeRange?: TimeRange;
  grouping?: Grouping;
  timePeriod?: Periods;
  timezone?: string;
  showDashedLinesForIncompleteBins?: boolean;
  startWeekAtTimeRangeStart?: boolean;
  isLoading?: boolean;
  disableHoverInteractions?: boolean;
  colorName: string;
  gradientName: string;
  isRate: boolean;
  animate?: boolean;
  chartOptions?: Options;
  showEdgeLabels?: boolean;
  preserveAspectRatio?: string;
  showUntrackedChangeAnnotation?: boolean;
  measureType: MeasureType;
  denominator: Measure | JourneyMetricsMeasure;
  enableExport?: boolean;
}

// We set this z index to be higher than the graph's so that the labels remain
// on top of it. Highcharts renders elements in order of z index.
const LABEL_Z_INDEX = 5;

function findBestLabelPosition(point: any, min: number, max: number, offset: number, plotHeight: number) {
  // Note: the Y coordinates in an SVG start at the top from 0 and then grow
  // downwards. So if coordinate A is larger than coordinate B, that means A is
  // below B.
  //
  // First, let's try putting the label right above the point
  let yPosition = point.plotY - offset;

  // If the position is larger than the min, this means it may overlap under the
  // peak of the graph.
  if (yPosition > min) {
    // If so, let's try positioning the label under the point. If that exceeds
    // the plotHeight (with a bit of padding), then let's fall back to
    // plotheight
    yPosition = Math.min(yPosition + 2 * offset, plotHeight - offset);

    // If positioning the label under the point might overlap with a valley in
    // the graph, we calculate the distances between the point and it's
    // extremes.
    // We will then put the point either over the peak, or under the valley,
    // depending on which of those is closest to the actual point.
    if (yPosition < max) {
      let minToValue = min - point.plotY;
      let maxToValue = max - point.plotY;

      if (maxToValue < minToValue) {
        yPosition = max + offset;
      } else {
        yPosition = min - offset;
      }
    }
  }

  return yPosition;
}

export default class HcSmoothGradientLineChart extends Component<HcSmoothGradientLineChartArgs> {
  get showEdgeLabels() {
    return this.args.showEdgeLabels ?? true;
  }

  get animate() {
    return this.args.animate ?? false;
  }

  get disableHoverInteractions() {
    return this.args.disableHoverInteractions ?? false;
  }

  get isRate() {
    return this.args.isRate ?? false;
  }

  get isAllZeroesOrEmpty() {
    let seriesData = this.args.data ?? [];

    return seriesData.length === 0 || seriesData.every((p) => p.y === 0 || p.y == null);
  }

  get hasZero() {
    let seriesData = this.args.data ?? [];
    return seriesData.some((p) => p.y === 0);
  }

  get timePeriod() {
    return REVERSE_PERIOD_KEYS[this.args.grouping ?? Grouping.Daily];
  }

  get timezone() {
    return this.args.timezone ?? 'Etc/UTC';
  }

  get chartOptions() {
    let seriesData = this.args.data ?? [];

    let { isAllZeroesOrEmpty, disableHoverInteractions, isRate, animate, showEdgeLabels } = this;

    let options: Options = {
      chart: {
        className: 'highcharts-transparent-background',
        animation: animate,
        height: 160,
        zoomType: null as any,
        // Need enough spacing at the bottom to not cut off lines when they are
        // 0, and to not cut off the hover marker. Since we don't show the X
        // axis on this chart, these would be cut off without extra spacing.
        spacing: [0, 0, 8, 0],
        events: {
          render(this: Chart) {
            let options = this.userOptions as any;
            if (options.edgeTextItems == null) {
              options.edgeTextItems = [];
            }
            options.edgeTextItems.forEach((item: SVGElement) => item.destroy());
            options.edgeTextItems = [];

            if (isAllZeroesOrEmpty && disableHoverInteractions) {
              return;
            }

            if (!showEdgeLabels) {
              return;
            }
            // This adds extra <text> elements for the first and last data
            // point, to show their values, since in this simple graph we don't
            // render the Y axis
            let [{ data }] = this.series;

            let firstPoint: any = data.find((d) => d.y != null);
            let lastPoint: any = [...data].reverse().find((d) => d.y != null);

            if (
              firstPoint == null ||
              lastPoint == null ||
              firstPoint.y == null ||
              lastPoint.y == null ||
              firstPoint.x === lastPoint.x
            ) {
              return;
            }

            let firstPointTextAnchor = 'start';
            let lastPointTextAnchor = 'end';

            // If the "first point" is not the actual first point in the data
            // array (e.g. when it's a rate graph and the first point has a
            // denominator of 0 and we don't render anything), change the text
            // alignment so that it's centered at the point, rather than
            // aligned to the edge.
            // Do the same for the last point.
            let firstPointIndex = data.findIndex((d) => d.x === firstPoint.x);
            let lastPointIndex = data.findIndex((d) => d.x === lastPoint.x);

            if (firstPointIndex !== 0) {
              firstPointTextAnchor = 'middle';
            }

            if (lastPointIndex !== data.length - 1) {
              lastPointTextAnchor = 'middle';
            }

            // Render the labels first so we can calculate their widths. The
            // initial position doesn't matter.
            let formatSpecifier = isRate ? '.1%' : ',';
            let firstPointY = isRate ? firstPoint.y / 100 : firstPoint.y;
            let firstPointText = this.renderer
              .text(formatNumber([firstPointY], { specifier: formatSpecifier }), firstPoint.plotX, 0)
              .attr({
                // eslint-disable-next-line spellcheck/spell-checker
                class: 'highcharts-data-label-smooth-gradient-chart',
                'text-anchor': firstPointTextAnchor,
                zIndex: LABEL_Z_INDEX,
              })
              .add();

            let lastPointY = isRate ? lastPoint.y / 100 : lastPoint.y;
            let lastPointText = this.renderer
              .text(formatNumber([lastPointY], { specifier: formatSpecifier }), lastPoint.plotX, 0)
              .attr({
                // eslint-disable-next-line spellcheck/spell-checker
                class: 'highcharts-data-label-smooth-gradient-chart',
                'text-anchor': lastPointTextAnchor,
                zIndex: LABEL_Z_INDEX,
              })
              .add();

            let firstPointBBox = firstPointText.getBBox();
            let lastPointBBox = lastPointText.getBBox();

            // Figure out how many pixels wide the labels are, and add a small
            // margin
            let margin = 8;
            let firstPointThreshold = Math.ceil(firstPointBBox.width) + margin;
            let lastPointThreshold = Math.ceil(lastPointBBox.width) + margin;
            let lastPixelRange = this.plotWidth - lastPointThreshold;

            // Find all points in the graph that fall within the range of where
            // the labels will be rendered (so we can see where it may
            // potentially overlap)
            let pointsInStartPixelRange = this.series[0].data
              .filter((p: any) => p.plotX <= firstPointThreshold)
              .map((p: any) => p.plotY);
            let pointsInLastPixelRange = this.series[0].data
              .filter((p: any) => p.plotX >= lastPixelRange)
              .map((p: any) => p.plotY);

            // Find the extremes of the range
            let firstPointMinY = getMinOfArray(pointsInStartPixelRange);
            let firstPointMaxY = getMaxOfArray(pointsInStartPixelRange);
            let lastPointMinY = getMinOfArray(pointsInLastPixelRange);
            let lastPointMaxY = getMaxOfArray(pointsInLastPixelRange);

            let yOffset = 10;

            // Find the best position for the label to minimize overlap with the
            // actual graph line
            let firstLabelYPosition = findBestLabelPosition(
              firstPoint,
              firstPointMinY,
              firstPointMaxY,
              yOffset,
              this.plotHeight,
            );
            let lastLabelYPosition = findBestLabelPosition(
              lastPoint,
              lastPointMinY,
              lastPointMaxY,
              yOffset,
              this.plotHeight,
            );

            let hasVerticalOverlap =
              firstPointBBox.y + firstPointBBox.height >= lastPointBBox.y &&
              lastPointBBox.y + lastPointBBox.height >= firstPointBBox.y;
            let hasHorizontalOverlap = firstPoint.plotX + firstPointBBox.width >= lastPoint.plotX;

            if (hasVerticalOverlap && hasHorizontalOverlap) {
              // Adding 10 to add a little bit of extra margin between the
              // labels
              let overlapDistance = firstPoint.plotX + firstPointBBox.width - lastPoint.plotX + 10;

              if (firstPoint.x === data[0].x) {
                // If the first label is attached to the very first data point
                // in the graph, don't move it to the left. Only move the next
                // point
                lastPointText.attr({
                  x: lastPoint.plotX + overlapDistance,
                });
              } else if (lastPoint.x === data[data.length - 1].x) {
                // Similarly, if the seond label is the last data point, only
                // move the previous label
                firstPointText.attr({
                  x: firstPoint.plotX - overlapDistance,
                });
              } else {
                // If neither label is an edge data point, move both of them an
                // equal distance
                firstPointText.attr({
                  x: firstPoint.plotX - overlapDistance / 2,
                });
                lastPointText.attr({
                  x: lastPoint.plotX + overlapDistance / 2,
                });
              }
            }

            // Apply the positions to the labels. dominant-baseline is
            // responsible for vertical alignment of the text, so that the gaps
            // between line and text remain consistent, whether it's render
            // below or above the line
            firstPointText.attr({
              y: firstLabelYPosition,
              'dominant-baseline': firstLabelYPosition - firstPoint.plotY > 0 ? 'text-before-edge' : 'text-after-edge',
            });

            lastPointText.attr({
              y: lastLabelYPosition,
              'dominant-baseline': lastLabelYPosition - lastPoint.plotY > 0 ? 'text-before-edge' : 'text-after-edge',
            });

            options.edgeTextItems.push(firstPointText, lastPointText);
          },
        },
      },
      series: [
        {
          type: 'areaspline',
          name: this.args.title,
          threshold: this.hasZero ? 0 : null,
          data: seriesData as any,
          animation: false,
          clip: false,
          colorIndex: this.args.colorName as any,
          className: `highcharts-${this.args.gradientName}`,
          marker: {
            enabled: false,
            radius: 2,
          },
          states: {
            hover: {
              enabled: !(isAllZeroesOrEmpty && disableHoverInteractions),
              halo: {
                size: 8,
              },
            },
          },
          custom: {
            measureType: this.args.measureType,
            denominator: this.args.denominator,
          },
        },
      ],
      defs: {
        gradient: {
          tagName: 'linearGradient',
          id: this.args.gradientName,
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 1,
          children: [
            {
              tagName: 'stop',
              offset: 0,
            },
            {
              tagName: 'stop',
              offset: 1,
            },
          ],
        },
      } as any,
      time: {
        timezone: this.timezone,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: !(isAllZeroesOrEmpty && disableHoverInteractions),
        outside: false,
        valueDecimals: isRate ? 1 : 0,
        valueSuffix: isRate ? '%' : '',
        hideDelay: 250,
      },
      xAxis: {
        type: 'datetime',
        startOfWeek: 1,
        className: 'highcharts-line-width-0',
        crosshair: !(isAllZeroesOrEmpty && disableHoverInteractions),
        visible: false,
      },
      yAxis: {
        allowDecimals: false,
        maxPadding: 0.2,
        tickAmount: this.hasZero ? 2 : undefined,
        visible: false,
        min: this.hasZero ? 0 : null,
      },
    };

    if (this.args.showDashedLinesForIncompleteBins) {
      let timestamps = seriesData.map((p) => p.x);
      let adjustedRange = {
        start: moment.tz(`${this.args.timeRange?.start?.format('YYYY-MM-DD')} 00:00:00`, this.timezone),
        end: moment.tz(`${this.args.timeRange?.end?.format('YYYY-MM-DD')} 00:00:00`, this.timezone),
      };

      options = addDashedZonesToIncompleteDataPoints(
        options,
        timestamps,
        adjustedRange,
        this.args.grouping ?? Grouping.Daily,
      );
    }

    return merge({}, options, this.args.chartOptions ?? {});
  }
}
